import React, { FormEvent, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FormInputSearch } from 'src/components/Search/type';
import {
  getBoolean,
  removeItem,
  getItem,
} from 'src/helpers/common/localStorage';
import validators from 'src/helpers/validators';
import useForm from 'src/hooks/useForm';
import { Cart } from 'src/model/Cart';
import Client from 'src/model/User';
import { api } from 'src/services/api';
import { websocketService } from 'src/services/websocket';
import { REACT_APP_AUTH, REACT_APP_USER } from 'src/utils/config';
import { AuthWrapperUI } from './ui';
import { AlertMessage } from 'src/model/AlertMessage';
import { trackTikTokEvent } from 'src/pixel/tiktok';

interface AuthWrapperProps {
  children: React.ReactNode;
}

type RepaymentEventType =
  | 'repayment:request'
  | 'repayment:analysis'
  | 'repayment:solicitation'
  | 'repayment:completed';

type WsEvent =
  | {
      eventType:
        | 'cart:update'
        | 'cart:delete'
        | 'chat:new_message'
        | 'notifications:update';
      payload?: unknown;
    }
  | {
      eventType: RepaymentEventType;
      payload: {
        repaymentId: string;
        orderId: string;
        sellerId: string;
        clientId: string;
        adminId?: string;
        at?: string;
      };
    };

export const AuthWrapper = ({ children }: AuthWrapperProps) => {
  const signed = getBoolean(String(REACT_APP_AUTH));
  const [cartCount, setCartCount] = useState<number>(0);
  const [carts, setCarts] = useState<Cart[]>([]);
  const [alertMessage, setAlertMessage] = useState<AlertMessage[]>([]);
  const [client, setClient] = useState<Client>();
  const history = useHistory();

  const getUser = async (): Promise<void> => {
    if (!signed) return;
    try {
      const response =
        await api.get<Pick<Client, 'id' | 'cart'>>(`/user/session`);
      if (response.status === 200) {
        setClient(response.data as Client);
        getNotifications(response.data.id);
        if (response.data.cart && response.data.cart.length > 0) {
          setCartCount(response.data.cart.length);
          setCarts(response.data.cart);
        } else {
          setCartCount(0);
          setCarts([]);
        }
      }
    } catch {
      console.error('Erro ao buscar usuário autenticado');
    }
  };

  const getNotifications = async (id?: string): Promise<void> => {
    const idClient: string | undefined = id ?? client?.id;
    if (!idClient) return;
    try {
      const alert = await api.get<AlertMessage[]>(
        `/alertMessage/findAll/${idClient}`
      );
      setAlertMessage(alert.data ?? []);
    } catch {
      console.log('Erro ao buscar notificações');
    }
  };

  const {
    formData,
    formErrors,
    setErrors: setError,
    onChangeFormInput,
    isFormValid,
    resetForm,
  } = useForm({
    initialData: { search: '' },
    validators: { search: [validators.required] },
  });

  const handleOnSubmitSearch = (e: FormEvent) => {
    e.preventDefault();
    if (isFormValid()) {
      const keyword = formData[FormInputSearch.search];
      if (keyword && keyword.trim()) {
        trackTikTokEvent('Search', {
          search_string: keyword.trim(),
        });
      }
      history.push(`/products-find/${keyword}`);
    } else {
      setError({ [FormInputSearch.search]: ['Algo deu errado...'] });
      resetForm();
    }
  };

  const handleLinkClick = (href: string): void => {
    history.push(href);
  };

  const handleOnSubmitLogout = () => {
    removeItem(String(REACT_APP_AUTH));
    removeItem(String(REACT_APP_USER));
    websocketService.disconnect();
    history.replace('/');
  };

  useEffect(() => {
    getUser();
  }, [signed]);

  useEffect(() => {
    if (!signed) return;
    const user = getItem(String(REACT_APP_USER));
    const currentId: string | undefined = client?.id ?? user?.id;
    if (!currentId) return;

    websocketService.connect(currentId);

    const handleMessage = (data: WsEvent) => {
      console.log('🔔 Evento recebido via WebSocket:', data);
      if (!data || typeof (data as any).eventType !== 'string') return;
      const t = data.eventType;

      if (t === 'cart:update' || t === 'cart:delete') {
        getUser();
        return;
      }

      if (t === 'chat:new_message') {
        return;
      }

      if (t === 'notifications:update') {
        getNotifications();
        return;
      }

      if (
        t === 'repayment:request' ||
        t === 'repayment:analysis' ||
        t === 'repayment:solicitation' ||
        t === 'repayment:completed'
      ) {
        const p = (data as Extract<WsEvent, { eventType: RepaymentEventType }>)
          .payload;
        if (!p) return;

        if (t === 'repayment:request') {
          if (p.sellerId === currentId) getNotifications();
          return;
        }

        if (t === 'repayment:analysis') {
          if (p.clientId === currentId) getNotifications();
          return;
        }

        if (t === 'repayment:solicitation') {
          if (p.adminId && p.adminId === currentId) getNotifications();
          return;
        }

        if (t === 'repayment:completed') {
          if (p.clientId === currentId || p.sellerId === currentId)
            getNotifications();
          return;
        }
      }
    };

    websocketService.addListener(handleMessage);
    return () => {
      websocketService.removeListener(handleMessage);
    };
  }, [signed, client?.id]);

  return (
    <AuthWrapperUI
      signed={signed}
      formData={formData}
      formErrors={formErrors}
      onChangeFormInput={onChangeFormInput}
      onSubmitSearch={handleOnSubmitSearch}
      onLinkClick={handleLinkClick}
      onSubmitLogout={handleOnSubmitLogout}
      cartCount={cartCount}
      carts={carts}
      alertMessage={alertMessage}
    >
      {children}
    </AuthWrapperUI>
  );
};
